import { styled } from 'opensolar-ui'
import { CSSProperties, ReactNode } from 'react'

export const SHOP_PAGE_MAX_WIDTH = 1440

export const SHOP_PAGE_SURFACE_COLOR = '#F6F9FC'
export const SHOP_PAGE_HORIZONTAL_PADDING = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
} as const

interface SurfaceStyledProps {
  $surfaceColor?: string
}

const Surface = styled('div', {
  name: 'ShopPageSurface',
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<SurfaceStyledProps>(({ $surfaceColor }) => ({
  width: '100%',
  ...($surfaceColor && {
    background: $surfaceColor,
    minHeight: '100vh',
  }),
}))

interface InnerStyledProps {
  $maxWidth?: number | string
  $disableVerticalPadding?: boolean
}

const Inner = styled('div', {
  name: 'ShopPageContainer',
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<InnerStyledProps>(({ theme, $maxWidth, $disableVerticalPadding }) => ({
  width: '100%',
  maxWidth: $maxWidth ?? SHOP_PAGE_MAX_WIDTH,
  marginLeft: 'auto',
  marginRight: 'auto',
  boxSizing: 'border-box',
  paddingTop: $disableVerticalPadding ? 0 : 24,
  paddingBottom: $disableVerticalPadding ? 0 : 24,
  paddingLeft: SHOP_PAGE_HORIZONTAL_PADDING.xs,
  paddingRight: SHOP_PAGE_HORIZONTAL_PADDING.xs,
  [theme.breakpoints.up('sm')]: {
    paddingLeft: SHOP_PAGE_HORIZONTAL_PADDING.sm,
    paddingRight: SHOP_PAGE_HORIZONTAL_PADDING.sm,
  },
  [theme.breakpoints.up('md')]: {
    paddingLeft: SHOP_PAGE_HORIZONTAL_PADDING.md,
    paddingRight: SHOP_PAGE_HORIZONTAL_PADDING.md,
  },
  [theme.breakpoints.up('lg')]: {
    paddingLeft: SHOP_PAGE_HORIZONTAL_PADDING.lg,
    paddingRight: SHOP_PAGE_HORIZONTAL_PADDING.lg,
  },
  [theme.breakpoints.up('xl')]: {
    paddingLeft: SHOP_PAGE_HORIZONTAL_PADDING.xl,
    paddingRight: SHOP_PAGE_HORIZONTAL_PADDING.xl,
  },
}))

export interface ShopPageContainerProps {
  children: ReactNode
  surfaceColor?: string | null
  maxWidth?: number | string
  disableVerticalPadding?: boolean
  className?: string
  style?: CSSProperties
  id?: string
  'data-testid'?: string
}

const ShopPageContainer = ({
  children,
  surfaceColor = SHOP_PAGE_SURFACE_COLOR,
  maxWidth,
  disableVerticalPadding,
  className,
  style,
  id,
  'data-testid': dataTestId,
}: ShopPageContainerProps) => {
  return (
    <Surface $surfaceColor={surfaceColor ?? undefined}>
      <Inner
        id={id}
        className={className}
        style={style}
        data-testid={dataTestId}
        $maxWidth={maxWidth}
        $disableVerticalPadding={disableVerticalPadding}
      >
        {children}
      </Inner>
    </Surface>
  )
}

export default ShopPageContainer
